/**
 * Selecting several cards (ADR 0194, decision 5), as the owner's "Block
 * Interactions" prototype has it (its `selToggle`, `selShift`, `selExtend`
 * and `selActions`). Pure functions of the board, so the day view only wires
 * them to the mouse, the keys and the API.
 *
 * - **Ctrl+click** (⌘ on a Mac), or Ctrl+Space, selects a card or unselects
 *   it, and it becomes the anchor.
 * - **Shift+click**, or Shift+↑ ↓, selects the range from the anchor to the
 *   card, within the anchor's column, keeping what was selected before the
 *   range began. In another column, it's just that card, and the new anchor.
 * - **What applies to them all** (`bulkActions`): an action is left out when
 *   none of the selection could take part, and greyed with a short reason
 *   when only some could.
 *
 * Only a block's card or a ticket's (ADR 0189) can be selected: never the
 * dashed cards of time entered straight in Autotask.
 */
import { formatClockRange } from "../../lib/dates";
import { formatPhone } from "../../lib/phones";
import { type BoardCard, cardColumn } from "./board";
import { type BlockView, COLUMNS, type ColumnId, needsYou, vendorParties } from "./model";
import { type TicketView, canClose } from "./tickets";

/** A card that can be selected, and what the bulk actions need of it. */
export interface PickableCard {
  /** `block:<id>` for a block's card, `ticket:<Autotask ID>` for a ticket's. */
  key: string;
  column: ColumnId;
  /** Its client's name, as the card shows it. */
  title: string;
  /** A block's card: its block. A ticket's card: its blocks, in time order. */
  blocks: readonly BlockView[];
  /** A logged card's ticket; null for a block still to log, or a logged one whose ticket isn't known. */
  ticket: TicketView | null;
  /** The card itself, for its menu. */
  card: BoardCard;
}

/** A card's key, or null for one that can't be selected (a dashed card). */
export function pickKey(card: BoardCard): string | null {
  switch (card.kind) {
    case "block":
      return `block:${card.block.id}`;
    case "ticket":
      return card.ticket.key;
    case "entry":
      return null;
  }
}

/** The board's cards that can be selected, column by column, each column's in the board's order. */
export function pickableCards(cards: readonly BoardCard[]): PickableCard[] {
  const out: PickableCard[] = [];
  for (const column of COLUMNS) {
    for (const card of cards) {
      const key = pickKey(card);
      if (key === null || cardColumn(card) !== column.id) {
        continue;
      }
      if (card.kind === "block") {
        out.push({ key, column: column.id, title: card.block.title, blocks: [card.block], ticket: card.block.ticket, card });
      } else if (card.kind === "ticket") {
        out.push({ key, column: column.id, title: card.ticket.title, blocks: card.ticket.blocks, ticket: card.ticket.ticket, card });
      }
    }
  }
  return out;
}

/** The keys of a column's cards, in order: what a range runs along. */
export function columnKeys(cards: readonly PickableCard[], column: ColumnId): string[] {
  return cards.filter((c) => c.column === column).map((c) => c.key);
}

/** What's selected, the anchor a range runs from, and what was selected before the range began. */
export interface Selection {
  readonly keys: ReadonlySet<string>;
  readonly anchor: string | null;
  readonly base: ReadonlySet<string>;
}

export const NO_SELECTION: Selection = { keys: new Set(), anchor: null, base: new Set() };

/** This card is the anchor now, with what's selected kept as the range's base (a plain click, too). */
export function anchorAt(selection: Selection, key: string): Selection {
  if (selection.anchor === key && sameSet(selection.base, selection.keys)) {
    return selection;
  }
  return { keys: selection.keys, anchor: key, base: new Set(selection.keys) };
}

/** Ctrl+click or Ctrl+Space: selected, or unselected; it's the anchor either way. */
export function toggle(selection: Selection, key: string): Selection {
  const keys = new Set(selection.keys);
  if (keys.has(key)) {
    keys.delete(key);
  } else {
    keys.add(key);
  }
  return { keys, anchor: key, base: new Set(keys) };
}

/**
 * Shift+click: from the anchor to this card, in the anchor's column, with
 * what was selected before the range. With no anchor in this card's column,
 * just this card is added, and it's the anchor.
 */
export function rangeTo(selection: Selection, key: string, column: readonly string[]): Selection {
  const { anchor } = selection;
  const from = anchor === null ? -1 : column.indexOf(anchor);
  const to = column.indexOf(key);
  if (from < 0 || to < 0) {
    const keys = new Set(selection.keys).add(key);
    return { keys, anchor: key, base: new Set(keys) };
  }
  const range = column.slice(Math.min(from, to), Math.max(from, to) + 1);
  return { keys: new Set([...selection.base, ...range]), anchor, base: selection.base };
}

/**
 * Shift+↑ ↓ from `from` to the card above or below it: the range from the
 * anchor, which is `from` when the anchor isn't in this column.
 */
export function extend(selection: Selection, from: string, to: string, column: readonly string[]): Selection {
  const anchored =
    selection.anchor !== null && column.includes(selection.anchor) ? selection : anchorAt(selection, from);
  return rangeTo(anchored, to, column);
}

/** Without the cards that left the board: the same selection when none did. */
export function prune(selection: Selection, live: ReadonlySet<string>): Selection {
  const gone = (key: string) => !live.has(key);
  const anchorGone = selection.anchor !== null && gone(selection.anchor);
  if (![...selection.keys].some(gone) && ![...selection.base].some(gone) && !anchorGone) {
    return selection;
  }
  return {
    keys: new Set([...selection.keys].filter((k) => !gone(k))),
    anchor: anchorGone ? null : selection.anchor,
    base: new Set([...selection.base].filter((k) => !gone(k))),
  };
}

/** Just these selected, the first of them the anchor: what's left after part of a bulk action failed. */
export function only(keys: readonly string[]): Selection {
  return { keys: new Set(keys), anchor: keys[0] ?? null, base: new Set(keys) };
}

function sameSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  return a.size === b.size && [...a].every((k) => b.has(k));
}

/** The selected cards, in the board's order. */
export function pickedCards(cards: readonly PickableCard[], selection: Selection): PickableCard[] {
  return cards.filter((c) => selection.keys.has(c.key));
}

// ---- What applies to the whole selection ----

export type BulkActionId = "dismiss" | "merge" | "vendor" | "close";

export interface BulkAction {
  id: BulkActionId;
  label: string;
  /** Every selected card can take part. */
  ok: boolean;
  /** Why not, when only some could: "different clients". */
  why: string | null;
}

/** A card of one block still to log: open or ready, never sent (section 9.4). */
function toLog(card: PickableCard): BlockView | null {
  const [block] = card.blocks;
  return card.blocks.length === 1 && block !== undefined && block.canDismiss ? block : null;
}

/** A Needs you block's card (ADR 0194 decision 6): what can be a vendor's. */
function needsYouBlock(card: PickableCard): BlockView | null {
  const block = toLog(card);
  return block !== null && needsYou(block) ? block : null;
}

/** Its ticket, while it can be closed from here: open, or a close that failed (ADR 0182). */
function closable(card: PickableCard): TicketView | null {
  return card.column === "open" && card.ticket !== null && canClose(card.ticket.state) ? card.ticket : null;
}

/**
 * Why several blocks still to log can't merge into one, by ADR 0118's rule
 * (`mergeCheck`) for each pair: all of one client, or with no client; two
 * without a client only from the same number or company. Null: they can.
 */
export function mergeRefusal(blocks: readonly BlockView[]): string | null {
  if (blocks.length < 2) {
    return "two or more";
  }
  const clients = new Set(blocks.flatMap((b) => (b.clientId === null ? [] : [b.clientId])));
  if (clients.size > 1) {
    return "different clients";
  }
  if (clients.size === 0) {
    const [first] = blocks;
    const one = first !== undefined && first.groupKey !== null && blocks.every((b) => b.groupKey === first.groupKey);
    return one ? null : "none has a client";
  }
  return null;
}

/**
 * What applies to the selection (the prototype's `selActions`), in the bar's
 * order: Dismiss ▾, Merge into one, Mark as vendor…, Close tickets….
 */
export function bulkActions(picked: readonly PickableCard[]): BulkAction[] {
  const actions: BulkAction[] = [];
  const blocks = picked.map(toLog);
  const someToLog = blocks.some((b) => b !== null);
  const allToLog = blocks.every((b) => b !== null);
  if (someToLog) {
    actions.push({ id: "dismiss", label: "Dismiss", ok: allToLog, why: allToLog ? null : "only blocks still to log" });
    const why = allToLog ? mergeRefusal(blocks) : "only blocks still to log";
    actions.push({ id: "merge", label: "Merge into one", ok: why === null, why });
  }
  const needs = picked.map(needsYouBlock);
  if (needs.some((b) => b !== null)) {
    const why = !needs.every((b) => b !== null)
      ? "Needs you blocks only"
      : needs.some((b) => vendorOf([b]).length === 0)
        ? "one has no email or calls"
        : null;
    actions.push({ id: "vendor", label: "Mark as vendor…", ok: why === null, why });
  }
  if (picked.some((c) => c.column === "open")) {
    const inOpen = picked.every((c) => c.column === "open");
    const open = picked.filter((c) => c.column === "open");
    const tickets = new Set(open.map((c) => (c.ticket === null ? c.key : String(c.ticket.ticketId))));
    const why = !inOpen ? "Logged - Open tickets only" : picked.every((c) => closable(c) !== null) ? null : "one can't be closed now";
    actions.push({ id: "close", label: tickets.size > 1 ? "Close tickets…" : "Close ticket…", ok: why === null, why });
  }
  return actions;
}

/** The selection's blocks still to log, in the board's order: what a bulk dismiss or merge takes. */
export function blocksToLog(picked: readonly PickableCard[]): BlockView[] {
  return picked.flatMap((c) => {
    const block = toLog(c);
    return block === null ? [] : [block];
  });
}

/** The selection's tickets that can be closed, each once, in the board's order. */
export function ticketsToClose(picked: readonly PickableCard[]): { ticket: TicketView; card: PickableCard }[] {
  const seen = new Set<number>();
  const out: { ticket: TicketView; card: PickableCard }[] = [];
  for (const card of picked) {
    const ticket = closable(card);
    if (ticket !== null && !seen.has(ticket.ticketId)) {
      seen.add(ticket.ticketId);
      out.push({ ticket, card });
    }
  }
  return out;
}

function byStart(a: BlockView, b: BlockView): number {
  return Date.parse(a.startedAt) - Date.parse(b.startedAt) || a.id.localeCompare(b.id);
}

/**
 * How several blocks merge into one, a pair at a time through the merge API
 * (ADR 0118): into the earliest, which is the one kept (ADR 0062). When it
 * has no client, a block with one goes first, so it takes that client before
 * any other without one joins it (two without a client from different
 * numbers never merge).
 */
export function mergeOrder(blocks: readonly BlockView[]): { keep: BlockView; others: BlockView[] } | null {
  const sorted = [...blocks].sort(byStart);
  const [keep, ...rest] = sorted;
  if (keep === undefined) {
    return null;
  }
  const others =
    keep.clientId === null
      ? [...rest.filter((b) => b.clientId !== null), ...rest.filter((b) => b.clientId === null)]
      : rest;
  return { keep, others };
}

/** A vendor to remember: an email's domain, or a call's number (E.164, and as it's shown). */
export interface VendorParty {
  kind: "email" | "calls";
  value: string;
  shown: string;
}

/** Who the blocks were with, each once, as the Vendor… pane lists them (ADR 0194 decision 6). */
export function vendorOf(blocks: readonly BlockView[]): VendorParty[] {
  const out: VendorParty[] = [];
  const seen = new Set<string>();
  for (const block of blocks) {
    const { domains, numbers } = vendorParties(block);
    for (const value of domains) {
      if (!seen.has(`email:${value}`)) {
        seen.add(`email:${value}`);
        out.push({ kind: "email", value, shown: value });
      }
    }
    for (const value of numbers) {
      if (!seen.has(`calls:${value}`)) {
        seen.add(`calls:${value}`);
        out.push({ kind: "calls", value, shown: formatPhone(value) });
      }
    }
  }
  return out;
}

const COLUMN_WORDS: Record<ColumnId, string> = {
  needs: "Needs you",
  ready: "Ready",
  open: "Logged - Open",
  closed: "Closed",
};

/** The bulk menu's header line: "1 Needs you · 1 Ready". */
export function selectionSummary(picked: readonly PickableCard[]): string {
  return COLUMNS.map((c) => [c.id, picked.filter((p) => p.column === c.id).length] as const)
    .filter(([, n]) => n > 0)
    .map(([id, n]) => `${String(n)} ${COLUMN_WORDS[id]}`)
    .join(" · ");
}

/**
 * Where keyboard focus goes after a bulk action took these cards off the
 * board (the prototype's `selNear`): the card after the last of them in the
 * first one's column, else the one before; else the board's first card left.
 */
export function nearCard(cards: readonly PickableCard[], gone: readonly string[]): string | null {
  const away = new Set(gone);
  const first = cards.find((c) => away.has(c.key));
  if (first === undefined) {
    return null;
  }
  const column = columnKeys(cards, first.column);
  const last = Math.max(...column.flatMap((k, i) => (away.has(k) ? [i] : [])));
  return (
    column.slice(last + 1).find((k) => !away.has(k)) ??
    column
      .slice(0, last)
      .reverse()
      .find((k) => !away.has(k)) ??
    cards.find((c) => !away.has(c.key))?.key ??
    null
  );
}

/** "A, B and C". */
export function andList(items: readonly string[]): string {
  if (items.length < 2) {
    return items.join("");
  }
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1] ?? ""}`;
}

/** A block, named by its times, for what a bulk action says: "9:00–9:15". */
export function timesOf(block: BlockView, timeZone: string): string {
  return formatClockRange(block.startedAt, block.endedAt, timeZone);
}

export function count(n: number, one: string, many: string): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}
